import { Alert, Button, Group, Modal, PasswordInput, Radio, Stack, Switch, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MIN_PASSWORD_LENGTH, type UserDto } from '@mexp/shared';
import { errorMessage, http } from '@/api/client';

export function SetPasswordModal({
  user,
  onClose,
  onTemporary,
  onDone,
}: {
  user: UserDto | null;
  onClose: () => void;
  onTemporary: (password: string) => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { mode: 'temporary' as 'temporary' | 'set', password: '', requirePasswordChange: true },
    validate: {
      password: (v, values) => (values.mode === 'set' && v.length < MIN_PASSWORD_LENGTH ? `Use at least ${MIN_PASSWORD_LENGTH} characters` : null),
    },
  });

  useEffect(() => {
    if (user) {
      form.reset();
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      http.post<{ temporaryPassword: string | null }>(`/users/${user!.id}/password`, {
        password: values.mode === 'set' ? values.password : undefined,
        requirePasswordChange: values.mode === 'set' ? values.requirePasswordChange : true,
      }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
      if (res.temporaryPassword) onTemporary(res.temporaryPassword);
      else onDone();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Modal opened={!!user} onClose={onClose} title={user ? `Password for ${user.fullName}` : ''}>
      <form onSubmit={form.onSubmit((v) => save.mutate(v))} noValidate>
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
              {error}
            </Alert>
          )}
          <Text fz="sm" c="dimmed">
            This signs them out everywhere and unlocks the account if it was locked.
          </Text>
          <Radio.Group {...form.getInputProps('mode')}>
            <Stack gap={6}>
              <Radio value="temporary" label="Generate a temporary password" />
              <Radio value="set" label="Set a specific password" />
            </Stack>
          </Radio.Group>
          {form.values.mode === 'set' && (
            <>
              <PasswordInput label="New password" autoComplete="new-password" {...form.getInputProps('password')} />
              <Switch label="Ask them to change it at next sign-in" {...form.getInputProps('requirePasswordChange', { type: 'checkbox' })} />
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {form.values.mode === 'temporary' ? 'Generate password' : 'Set password'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
